import { useEffect, useMemo, useState } from 'react'
import { Box, GlobalStyles, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined'
import TitleOutlinedIcon from '@mui/icons-material/TitleOutlined'
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined'
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import CheckIcon from '@mui/icons-material/Check'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/Schedule'
import type { SvgIconComponent } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { youTubeId, youTubeThumb } from '@/utils/youtube'
import type { EditableQuestion } from '@/components/questions/types'
import { askableQuestions } from './askable'
import type { QuestionTiming, StimulusDraft, StimulusType, StudyDraft } from './types'

type PreviewVariant = 'stimulus' | 'questions' | 'landing' | 'tour'

const TYPE_ICON: Record<StimulusType, SvgIconComponent> = {
  video: VideocamOutlinedIcon,
  image: ImageOutlinedIcon,
  audio: MicNoneOutlinedIcon,
  text: TitleOutlinedIcon,
  url: LinkOutlinedIcon,
  figma: TouchAppOutlinedIcon,
}

/** Create (and revoke) an object URL for a locally-picked file. */
function useObjectUrl(file: File | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!file) {
      setUrl(null)
      return
    }
    const u = URL.createObjectURL(file)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [file])
  return url
}

/** Dark "respondent preview" panel framing a small mobile mock of the
 *  respondent-facing screen for the current wizard step. It mirrors the actual
 *  draft (stimuli, questions, study name) so the researcher sees what
 *  respondents will get. The `tour` variant auto-plays the whole journey. */
export default function RespondentPreview({
  variant,
  draft,
  embedded = false,
}: {
  variant: PreviewVariant
  draft: StudyDraft
  /** Only the phone, without the dark panel and its header — for a parent
   *  card that brings its own (the Pro wizard's side column). */
  embedded?: boolean
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const isTour = variant === 'tour'

  const screen =
    variant === 'tour' ? (
      <PreviewTour draft={draft} coral={c.coral} />
    ) : variant === 'stimulus' ? (
      <PreviewStimulus draft={draft} coral={c.coral} />
    ) : variant === 'questions' ? (
      <PreviewQuestions draft={draft} />
    ) : (
      <PreviewLanding draft={draft} />
    )

  if (embedded) {
    return (
      <>
        <PreviewKeyframes />
        <Box
          sx={{
            width: 172,
            alignSelf: 'center',
            aspectRatio: '9 / 16',
            bgcolor: '#fff',
            borderRadius: '20px',
            border: '6px solid #1B1C38',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {screen}
        </Box>
      </>
    )
  }

  return (
    <Box sx={{ bgcolor: '#0B0C1E', borderRadius: `${theme.affexy.radii.md}px`, p: 2, color: '#fff', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <PreviewKeyframes />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <VisibilityOutlinedIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,.6)' }} />
        <Typography variant="overline" sx={{ fontSize: 11, color: 'rgba(255,255,255,.6)' }}>
          {t('wizard.preview.title')}
        </Typography>
        {isTour && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.75 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: 999, bgcolor: c.coral, animation: 'fwPulse 1.4s ease-in-out infinite' }} />
            <Typography sx={{ fontSize: 9.5, letterSpacing: '.08em', textTransform: 'uppercase', color: c.coral, fontWeight: 700 }}>
              {t('wizard.preview.live')}
            </Typography>
          </Box>
        )}
        <Typography sx={{ ml: 'auto', fontSize: 10.5, color: 'rgba(255,255,255,.4)' }}>
          {t('wizard.preview.device')}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 230,
          alignSelf: 'center',
          aspectRatio: '9 / 16',
          bgcolor: '#fff',
          borderRadius: '22px',
          border: '6px solid #1B1C38',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {screen}
      </Box>
    </Box>
  )
}

/** Keyframes used by the animated tour, injected once per preview panel. */
function PreviewKeyframes() {
  return (
    <GlobalStyles
      styles={{
        '@keyframes fwFade': {
          from: { opacity: 0, transform: 'translateY(6px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes fwTap': {
          '0%': { transform: 'scale(0)', opacity: 0 },
          '60%': { transform: 'scale(1.25)', opacity: 1 },
          '100%': { transform: 'scale(1)', opacity: 1 },
        },
        '@keyframes fwPulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.35 },
        },
        '@keyframes fwPop': {
          from: { transform: 'scale(0.6)', opacity: 0 },
          to: { transform: 'scale(1)', opacity: 1 },
        },
      }}
    />
  )
}

// ─────────────────────────────── Tour engine ───────────────────────────────

type Scene =
  | { kind: 'landing' }
  | { kind: 'consent' }
  | { kind: 'tech' }
  | { kind: 'stimulus'; index: number }
  | { kind: 'question'; qIndex: number; answered: boolean }
  | { kind: 'thanks' }

const SCENE_MS = {
  landing: 3000,
  consent: 2800,
  tech: 2600,
  stimulus: 2600,
  ask: 1100,
  answer: 1600,
  thanks: 2600,
}

function sceneMs(s: Scene): number {
  if (s.kind === 'question') return s.answered ? SCENE_MS.answer : SCENE_MS.ask
  return SCENE_MS[s.kind]
}

/** A stable identity per *screen* — ask + answer of one question share it so the
 *  selection animates in place rather than re-mounting. */
function screenKey(s: Scene): string {
  if (s.kind === 'question') return `q-${s.qIndex}`
  if (s.kind === 'stimulus') return `s-${s.index}`
  return s.kind
}

/** Build the looped storyboard from the draft, mirroring the real respondent
 *  journey (see RespondentJourney): invitation → consent → then, depending on
 *  when questions are asked, either questions → camera check → stimulus, or
 *  camera check → stimulus → questions → thank-you. `questions` is the
 *  already-filtered askable list; up to 3 stimuli and 4 questions are shown. */
function buildScenes(
  stimuli: StimulusDraft[],
  questions: EditableQuestion[],
  timing: QuestionTiming,
): Scene[] {
  const stimulusScenes: Scene[] =
    stimuli.length === 0
      ? [{ kind: 'stimulus', index: 0 }]
      : stimuli.slice(0, 3).map((_, index) => ({ kind: 'stimulus', index }))

  const questionScenes: Scene[] = []
  const count = Math.min(questions.length, 4) // no mock filler — only real questions
  for (let qIndex = 0; qIndex < count; qIndex++) {
    questionScenes.push({ kind: 'question', qIndex, answered: false })
    questionScenes.push({ kind: 'question', qIndex, answered: true })
  }

  const tech: Scene = { kind: 'tech' }
  // The camera always records during the stimulus; questions move before it
  // when the researcher asks them up front.
  const middle: Scene[] =
    timing === 'start'
      ? [...questionScenes, tech, ...stimulusScenes]
      : [tech, ...stimulusScenes, ...questionScenes]

  return [{ kind: 'landing' }, { kind: 'consent' }, ...middle, { kind: 'thanks' }]
}

/** Deterministic "random" option pick that varies by loop and question, so the
 *  selected answer changes each pass without using a real RNG in render. */
function pickFor(loopCount: number, qIndex: number, len: number): number {
  if (len <= 0) return 0
  const h = (loopCount * 1103515245 + qIndex * 12345 + 7) >>> 0
  return h % len
}

/** Random-looking answer pick(s) for one question: a single option for
 *  single-style kinds, two distinct options for multi. */
function picksFor(loopCount: number, qIndex: number, kind: EditableQuestion['kind'], len: number): number[] {
  if (len <= 0) return []
  const first = pickFor(loopCount, qIndex, len)
  if (kind !== 'multi' || len < 2) return [first]
  const second = (first + 1 + pickFor(loopCount + 13, qIndex + 5, len - 1)) % len
  return [first, second]
}

function PreviewTour({ draft, coral }: { draft: StudyDraft; coral: string }) {
  const askable = useMemo(
    () => askableQuestions(draft.questions, draft.demographics),
    [draft.questions, draft.demographics],
  )
  const scenes = useMemo(
    () => buildScenes(draft.stimuli, askable, draft.questionTiming),
    [draft.stimuli, askable, draft.questionTiming],
  )
  const [idx, setIdx] = useState(0)

  // Restart from the top whenever the storyboard changes (stimuli/questions edited).
  useEffect(() => setIdx(0), [scenes])

  useEffect(() => {
    const s = scenes[idx % scenes.length]
    const id = setTimeout(() => setIdx((n) => n + 1), sceneMs(s))
    return () => clearTimeout(id)
  }, [idx, scenes])

  const pos = idx % scenes.length
  const loopCount = Math.floor(idx / scenes.length)
  const scene = scenes[pos]

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <Box
        key={screenKey(scene)}
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', animation: 'fwFade 360ms ease both' }}
      >
        {scene.kind === 'landing' ? (
          <PreviewLanding draft={draft} />
        ) : scene.kind === 'consent' ? (
          <PreviewConsent draft={draft} />
        ) : scene.kind === 'tech' ? (
          <PreviewTech coral={coral} />
        ) : scene.kind === 'stimulus' ? (
          <PreviewStimulus draft={draft} coral={coral} index={scene.index} />
        ) : scene.kind === 'question' ? (
          <QuestionScene questions={askable} qIndex={scene.qIndex} answered={scene.answered} loopCount={loopCount} />
        ) : (
          <PreviewThanks />
        )}
      </Box>
    </Box>
  )
}

/** A single question scene: shows the question, then picks (and reveals) an
 *  answer once the scene is in its `answered` half. Shared by the tour and the
 *  questions-step preview. */
function QuestionScene({
  questions,
  qIndex,
  answered,
  loopCount,
}: {
  questions: EditableQuestion[]
  qIndex: number
  answered: boolean
  loopCount: number
}) {
  const { t } = useTranslation('dashboard')
  const q = questions[qIndex]
  if (!q) return null // storyboard outran an edit — next tick re-syncs
  const counter = t('wizard.preview.questionOf', { current: qIndex + 1, total: questions.length })
  // Pick only among the options the card actually renders (lists are capped at 5).
  const isScaleRow = q.kind === 'scale' || q.kind === 'nps'
  const optionCount = isScaleRow ? displayOptions(q).length : Math.min(displayOptions(q).length, 5)
  const picked = answered ? picksFor(loopCount, qIndex, q.kind, optionCount) : []

  return <QuestionCard q={q} counter={counter} picked={picked} typed={answered && q.kind === 'text'} tap />
}

// ──────────────────────────────── Stimulus ─────────────────────────────────

function PreviewStimulus({ draft, coral, index = 0 }: { draft: StudyDraft; coral: string; index?: number }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const stimuli = draft.stimuli
  const first: StimulusDraft | undefined = stimuli[index]
  const objectUrl = useObjectUrl(first?.file)

  const ytId = first?.type === 'url' ? youTubeId(first.url) : null
  const imageSrc = first?.type === 'image' ? objectUrl : ytId ? youTubeThumb(ytId) : null
  const videoSrc = first?.type === 'video' ? objectUrl : first?.type === 'url' && !ytId && first.url ? first.url : null
  const Icon = first ? TYPE_ICON[first.type] : ImageOutlinedIcon

  return (
    <Box sx={{ flex: 1, bgcolor: '#0B0C1E', position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 7.5, color: 'rgba(255,255,255,.5)', letterSpacing: '.1em', textTransform: 'uppercase' }}>
          {t('wizard.preview.recording')}
        </Typography>
        <Box sx={{ width: 5, height: 5, borderRadius: 999, bgcolor: coral, animation: 'fwPulse 1.4s ease-in-out infinite' }} />
      </Box>
      <Box sx={{ flex: 1, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          sx={{
            width: '100%',
            aspectRatio: '9 / 16',
            borderRadius: '8px',
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: imageSrc || videoSrc ? '#000' : 'linear-gradient(135deg,#0F1024,#5A1A18)',
          }}
        >
          {imageSrc ? (
            <Box component="img" src={imageSrc} alt={first?.name ?? ''} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : videoSrc ? (
            <Box component="video" src={videoSrc} muted playsInline autoPlay loop sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : first?.type === 'text' && first.payload?.trim() ? (
            <Typography sx={{ p: 1.25, fontSize: 9, lineHeight: 1.45, color: '#fff', textAlign: 'center', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 8, WebkitBoxOrient: 'vertical' }}>
              {first.payload}
            </Typography>
          ) : first ? (
            <Icon sx={{ fontSize: 26, color: 'rgba(255,255,255,.55)' }} />
          ) : (
            <Typography sx={{ px: 1.5, fontSize: 8.5, lineHeight: 1.5, color: 'rgba(255,255,255,.5)', textAlign: 'center' }}>
              {t('wizard.preview.emptyStimulus')}
            </Typography>
          )}
        </Box>
      </Box>
      {first && (
        <Box sx={{ px: '10px', pb: '8px' }}>
          <Typography sx={{ fontSize: 7.5, color: c.coral, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>
            {t('wizard.preview.stimulusOf', { current: index + 1, total: stimuli.length })}
          </Typography>
          {first.name?.trim() && (
            <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 9.5, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {first.name}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

// ──────────────────────────────── Questions ────────────────────────────────

/** Options to render for a question, synthesising scale/nps scales that have
 *  no author-edited option list. */
function displayOptions(q: EditableQuestion): string[] {
  if (q.kind === 'scale') {
    const labels = q.options.map((o) => o.text.trim()).filter(Boolean)
    return labels.length ? labels : ['1', '2', '3', '4', '5']
  }
  if (q.kind === 'nps') return Array.from({ length: 11 }, (_, i) => String(i))
  return q.options.map((o) => o.text.trim()).filter(Boolean)
}

/** Animated questions-step preview: loops through the currently selected
 *  questions (builder + metryczka), "answering" each with a pick that varies
 *  per pass — a mini respondent run-through of exactly what's configured.
 *  The questions step only mounts it when at least one question is askable. */
function PreviewQuestions({ draft }: { draft: StudyDraft }) {
  const questions = useMemo(
    () => askableQuestions(draft.questions, draft.demographics),
    [draft.questions, draft.demographics],
  )
  // Edits update the current scene in place — no restart, so the loop keeps
  // its position while the researcher types. Modulo keeps qIndex in range.
  const count = Math.max(questions.length, 1)
  const [idx, setIdx] = useState(0)

  const answered = idx % 2 === 1
  const ms = answered ? SCENE_MS.answer : SCENE_MS.ask
  useEffect(() => {
    const id = setTimeout(() => setIdx((n) => n + 1), ms)
    return () => clearTimeout(id)
  }, [idx, ms])

  const step = Math.floor(idx / 2)
  const qIndex = step % count
  const loopCount = Math.floor(step / count)

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box key={qIndex} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', animation: 'fwFade 360ms ease both' }}>
        <QuestionScene questions={questions} qIndex={qIndex} answered={answered} loopCount={loopCount} />
      </Box>
    </Box>
  )
}

/** Presentational question card for one preview scene.
 *  `picked` highlights answers (indices into the rendered options — one for
 *  single-style kinds, several for multi, empty for none);
 *  `tap` adds the little "just selected" check that pops in. */
function QuestionCard({
  q,
  counter,
  picked,
  typed = false,
  tap = false,
}: {
  q: EditableQuestion
  counter: string
  picked: number[]
  typed?: boolean
  tap?: boolean
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')

  const options = displayOptions(q)
  const isScaleRow = q.kind === 'scale' || q.kind === 'nps'

  return (
    <Box sx={{ flex: 1, bgcolor: '#fff', display: 'flex', flexDirection: 'column', p: 1.25, gap: 1, overflow: 'hidden' }}>
      <Typography sx={{ fontSize: 7, color: c.coral, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700 }}>
        {counter}
      </Typography>
      <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 12, lineHeight: 1.2, color: c.ink, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {q.text}
      </Typography>

      {q.kind === 'text' ? (
        <Box
          sx={{
            mt: 0.5,
            p: '10px',
            borderRadius: '8px',
            border: `1px solid ${typed ? c.coral : c.ink08}`,
            bgcolor: typed ? 'rgba(234,80,72,.04)' : c.gray50,
            fontSize: 9.5,
            color: typed ? c.ink : c.ink48,
            transition: 'background-color 320ms, border-color 320ms, color 320ms',
          }}
        >
          {typed ? t('wizard.preview.textAnswerSample') : t('wizard.preview.textAnswerPlaceholder')}
        </Box>
      ) : isScaleRow ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {options.map((option, i) => {
            const sel = picked.includes(i)
            return (
              <Box
                key={`${option}-${i}`}
                sx={{
                  minWidth: 18,
                  px: 0.5,
                  py: '5px',
                  textAlign: 'center',
                  borderRadius: '6px',
                  fontSize: 9,
                  fontWeight: sel ? 700 : 400,
                  color: sel ? '#fff' : c.ink,
                  border: `1px solid ${sel ? c.coral : c.ink08}`,
                  bgcolor: sel ? c.coral : '#fff',
                  transition: 'background-color 300ms, border-color 300ms, color 300ms',
                }}
              >
                {option}
              </Box>
            )
          })}
        </Box>
      ) : options.length ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
          {options.slice(0, 5).map((option, i) => {
            const sel = picked.includes(i)
            return (
              <Box
                key={`${option}-${i}`}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 0.5,
                  p: '7px 10px',
                  borderRadius: '8px',
                  fontSize: 10,
                  color: c.ink,
                  border: `1px solid ${sel ? c.coral : c.ink08}`,
                  bgcolor: sel ? 'rgba(234,80,72,.06)' : '#fff',
                  transition: 'background-color 300ms, border-color 300ms',
                  overflow: 'hidden',
                }}
              >
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {option}
                </Box>
                {sel && tap && <CheckIcon sx={{ fontSize: 12, color: c.coral, flexShrink: 0, animation: 'fwTap 360ms ease' }} />}
              </Box>
            )
          })}
        </Box>
      ) : null}
    </Box>
  )
}

// ───────────────────────────────── Landing ─────────────────────────────────

/** Brand lockup used at the top of the respondent-facing screens. */
function PreviewBrand() {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
      <Box
        component="img"
        src={`${import.meta.env.BASE_URL}sygnet.png`}
        alt=""
        sx={{ width: 12, height: 12, objectFit: 'contain', display: 'block' }}
      />
      <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 10.5, color: c.ink }}>
        FaceWise
      </Typography>
    </Box>
  )
}

/** One "what you'll need" row on the landing screen. */
function NeedRow({ icon, label }: { icon: React.ReactNode; label: string }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: '5px 7px', border: `1px solid ${c.ink08}`, borderRadius: '7px' }}>
      <Box sx={{ width: 18, height: 18, borderRadius: '5px', bgcolor: 'rgba(234,80,72,.1)', color: c.coral, display: 'grid', placeItems: 'center', flex: '0 0 18px' }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: 8.5, color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Box>
  )
}

function PreviewLanding({ draft }: { draft: StudyDraft }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')

  const title = draft.name.trim() || t('wizard.preview.invitationTitle')
  const client = draft.client.trim()

  return (
    <Box sx={{ flex: 1, bgcolor: '#fff', display: 'flex', flexDirection: 'column', p: 1.25, gap: 0.6, overflow: 'hidden' }}>
      <PreviewBrand />
      <Typography sx={{ fontSize: 7, color: c.coral, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', mt: 0.5 }}>
        {t('wizard.preview.invitation')}
      </Typography>
      <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 13, lineHeight: 1.15, color: c.ink, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {title}
      </Typography>
      {client && (
        <Box sx={{ alignSelf: 'flex-start', px: 0.75, py: '2px', borderRadius: 34, bgcolor: c.gray50, border: `1px solid ${c.ink08}`, fontSize: 7.5, color: c.ink72, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {client}
        </Box>
      )}
      <Typography sx={{ fontSize: 7, color: c.ink48, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', mt: 0.5 }}>
        {t('wizard.preview.needsTitle')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <NeedRow icon={<VideocamOutlinedIcon sx={{ fontSize: 11 }} />} label={t('wizard.preview.needCamera')} />
        <NeedRow icon={<WbSunnyOutlinedIcon sx={{ fontSize: 11 }} />} label={t('wizard.preview.needLight')} />
        <NeedRow icon={<ScheduleOutlinedIcon sx={{ fontSize: 11 }} />} label={t('wizard.preview.needTime')} />
      </Box>
      <Box sx={{ flex: 1 }} />
      {draft.requireAccessCode && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: c.ink56, fontSize: 8.5 }}>
          <LockOutlinedIcon sx={{ fontSize: 11 }} />
          {t('wizard.preview.accessCodeHint')}
        </Box>
      )}
      <Box sx={{ bgcolor: c.coral, color: '#fff', borderRadius: 34, py: '7px', textAlign: 'center', fontSize: 10, fontWeight: 500 }}>
        {t('wizard.preview.startButton')}
      </Box>
    </Box>
  )
}

// ───────────────────────────────── Consent ─────────────────────────────────

/** Consent screen (RODO): what's collected + the agreement, mirroring the real
 *  respondent consent step. */
function PreviewConsent({ draft }: { draft: StudyDraft }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')

  const collected = [
    t('wizard.preview.collectVideo'),
    t('wizard.preview.collectEmotions'),
    t('wizard.preview.collectAnswers'),
  ]

  return (
    <Box sx={{ flex: 1, bgcolor: '#fff', display: 'flex', flexDirection: 'column', p: 1.25, gap: 0.6, overflow: 'hidden' }}>
      <Typography sx={{ fontSize: 7, color: c.coral, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase' }}>
        {t('wizard.preview.consentStep')}
      </Typography>
      <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 12, lineHeight: 1.2, color: c.ink }}>
        {t('wizard.preview.consentTitle')}
      </Typography>
      <Typography sx={{ fontSize: 7, color: c.ink48, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', mt: 0.5 }}>
        {t('wizard.preview.collectTitle')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {collected.map((label) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: '5px 7px', border: `1px solid ${c.ink08}`, borderRadius: '7px' }}>
            <CheckIcon sx={{ fontSize: 11, color: c.coral, flex: '0 0 auto' }} />
            <Typography sx={{ fontSize: 8.5, color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ flex: 1 }} />
      {draft.requireAccessCode && (
        <Box sx={{ p: '5px 7px', borderRadius: '7px', bgcolor: c.gray50, border: `1px solid ${c.ink08}`, fontSize: 8, color: c.ink48 }}>
          {t('wizard.preview.accessCodeHint')}
        </Box>
      )}
      {[t('wizard.preview.consentAgree'), t('wizard.preview.consentRodo')].map((label) => (
        <Box key={label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6 }}>
          <Box sx={{ width: 11, height: 11, borderRadius: '3px', bgcolor: c.coral, display: 'grid', placeItems: 'center', flex: '0 0 11px', mt: '1px' }}>
            <CheckIcon sx={{ fontSize: 8, color: '#fff' }} />
          </Box>
          <Typography sx={{ fontSize: 7.5, lineHeight: 1.35, color: c.ink72 }}>
            {label}
          </Typography>
        </Box>
      ))}
      <Box sx={{ bgcolor: c.coral, color: '#fff', borderRadius: 34, py: '7px', textAlign: 'center', fontSize: 9.5, fontWeight: 500 }}>
        {t('wizard.preview.consentButton')}
      </Box>
    </Box>
  )
}

// ──────────────────────────────── Camera check ─────────────────────────────

/** Camera-check screen: a framed face preview with the "face detected" badge,
 *  mirroring the real respondent tech-check step. */
function PreviewTech({ coral }: { coral: string }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')

  return (
    <Box sx={{ flex: 1, bgcolor: '#fff', display: 'flex', flexDirection: 'column', p: 1.25, gap: 0.75, overflow: 'hidden' }}>
      <Typography sx={{ fontSize: 7, color: c.coral, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase' }}>
        {t('wizard.preview.techStep')}
      </Typography>
      <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 12, lineHeight: 1.2, color: c.ink }}>
        {t('wizard.preview.techTitle')}
      </Typography>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          borderRadius: '10px',
          border: `2px solid ${c.success}`,
          background: 'linear-gradient(135deg,#1B1C38,#2A2C52)',
          position: 'relative',
          display: 'grid',
          placeItems: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Face framing oval */}
        <Box sx={{ width: '42%', aspectRatio: '3 / 4', borderRadius: '50%', border: '1.5px dashed rgba(255,255,255,.45)' }} />
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.4,
            px: 0.75,
            py: '2px',
            borderRadius: 34,
            bgcolor: 'rgba(95,179,168,.92)',
            color: '#fff',
            fontSize: 7.5,
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          <CheckIcon sx={{ fontSize: 9 }} />
          {t('wizard.preview.faceDetected')}
        </Box>
        <Box sx={{ position: 'absolute', top: 6, right: 6, width: 6, height: 6, borderRadius: '50%', bgcolor: coral, animation: 'fwPulse 1.4s ease-in-out infinite' }} />
      </Box>
      <Box sx={{ bgcolor: c.coral, color: '#fff', borderRadius: 34, py: '7px', textAlign: 'center', fontSize: 9.5, fontWeight: 500 }}>
        {t('wizard.preview.techButton')}
      </Box>
    </Box>
  )
}

// ───────────────────────────────── Thank you ───────────────────────────────

function PreviewThanks() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')

  return (
    <Box sx={{ flex: 1, bgcolor: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', p: 1.5, gap: 1 }}>
      <Box
        sx={{
          width: 46,
          height: 46,
          borderRadius: '50%',
          bgcolor: 'rgba(95,179,168,.15)',
          display: 'grid',
          placeItems: 'center',
          animation: 'fwPop 420ms ease both',
        }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: 28, color: c.success }} />
      </Box>
      <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 15, color: c.ink }}>
        {t('wizard.preview.thanksTitle')}
      </Typography>
      <Typography sx={{ fontSize: 9.5, lineHeight: 1.45, color: c.ink56, maxWidth: 160 }}>
        {t('wizard.preview.thanksBody')}
      </Typography>
      <Box sx={{ mt: 0.5, bgcolor: c.coral, color: '#fff', borderRadius: 34, px: 1.75, py: '6px', fontSize: 9.5, fontWeight: 600 }}>
        {t('wizard.preview.seeResults')}
      </Box>
    </Box>
  )
}
